import { Box, Grid, Button, Typography, IconButton } from "@material-ui/core";
import { Form, Modal } from "@components";
import React, { useCallback, useContext } from "react";
import { useHistory } from "react-router";
import { UPDATE_FILTERS_DATA } from "../state";
import { TableContext } from "../table";
import { Search } from "./search-filters";
import { Actions } from "../../../screens/request/components/actions";
import CloseIcon from "@material-ui/icons/Close";
import { Api } from "@api";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

interface Props {
  onClickAdd: () => void;
}

type FormValues = {
  name: string;
  isActive: boolean;
};

type Key = keyof FormValues;

export const SystemTypeToolbar: React.FC<Props> = ({ onClickAdd }) => {
  const history = useHistory();
  const { dispatch } = useContext(TableContext);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState<
    boolean
  >(false);
  const [pending, setPending] = React.useState(false);
  const onSearchUpdate = useCallback(
    (term: string) => {
      dispatch({
        type: UPDATE_FILTERS_DATA,
        filtersData: { searchTerm: term },
      });
    },
    [dispatch]
  );

  const form = Form.useManager<FormValues>({
    name: "",
    isActive: false,
  });

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "name":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleAddButtonClick(): void {
    setPending(true);
    Api.systemtype
      .postSystemType({
        ...form.values,
      })
      .then(() => {
        setPending(false);
        setIsOpenConfirmationModal(false);
        form.reset();
        handleClose();
        onClickAdd();
      })
      .catch((error) => {
        setPending(false);
        // [TODO] Handle error
      });
  }

  function handleSubmitButtonClick(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          setIsOpenConfirmationModal(true);
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    form.reset();
    setOpen(false);
  };
  const styles = (theme: Theme) =>
    createStyles({
      root: {
        margin: 0,
        padding: theme.spacing(2),
      },
      closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
      },
    });

  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

  return (
    <Box mt={2} mb={2}>
      <Grid container direction="row" spacing={1} justify="space-between">
        <Grid item xs={"auto"}>
          <Actions>
            <Actions.Action onClick={handleClickOpen}>
              + Add New Business Units
            </Actions.Action>
          </Actions>
        </Grid>
        <Grid item xs={3}>
          <Search
            onSearchUpdate={onSearchUpdate}
            placeholder={"Search by Name"}
          ></Search>
        </Grid>
      </Grid>
      <Modal
        isOpen={isOpenConfirmationModal}
        positionButton="center"
        actions={[
          {
            label: "Cancel",
            variant: "outlined",
            handler: () => {
              setIsOpenConfirmationModal(false);
            },
          },
          {
            label: pending ? "Submitting..." : "Submit",
            handler: () => {
              handleAddButtonClick();
            },
            disabled: pending,
          },
        ]}
      >
        <Typography align="center" paragraph>
          Are you sure you want to add this Business Unit?
        </Typography>
      </Modal>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth={"sm"}
      >
        <DialogTitle id="form-dialog-title" onClose={handleClose}>
          Add New Business Unit
        </DialogTitle>
        <DialogContent>
          <Form>
            <Form.Textbox
              label="Business Unit"
              name="name"
              onChange={onChange}
              size={12}
              value={form.values.name}
              required
              error={form.errors.name}
            />
            <Form.Checkbox
              label="Is Active"
              name="isActive"
              onChange={onChange}
              size={6}
            />
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmitButtonClick} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
